import { Editor } from '@monaco-editor/react'
import Button from 'components/core/Button'
import Modal from 'components/core/Modal'
import PhoneNumberSelector from 'components/core/PhoneNumberSelector'
import { TopBar } from 'components/Layout/TopBar'
import { TopBarContent } from 'components/Layout/TopBarProvider'
import { Button as RACButton } from 'components/ui/Button'
import config from 'config'
import usePhoneNumbers from 'hooks/usePhoneNumbers'
import { useVoices } from 'hooks/useVoices'
import { useEffect, useState } from 'react'
import Select from 'react-dropdown-select'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { getApiKey } from 'utils/funcs/browser/getApiKey'
import { getAuthToken } from 'utils/funcs/browser/getAuthToken'
import { getOrgId } from 'utils/funcs/browser/getOrgId'
import ListeningGif from '../../../assets/gifs/listening-slow.gif'
import CloseIcon from '../../../assets/icons/close-icon-black.svg'
import CopyIcon from '../../../assets/icons/copy-icon.svg'
import ExpandIcon from '../../../assets/icons/Expandicon.svg'
import RefreshIcon from '../../../assets/icons/refresh-icon.svg'
import SettingIcon from '../../../assets/icons/setting-icon.svg'
import AnalysisPreset from '../Parameters/AnalysisPreset'
import Model from '../Parameters/Model'
import BackgroundDropdown from './components/BackgroundDropdown'
import BooleanDropdown from './components/BooleanDropdown'
import HookInputDropdown from './components/HookInputDropdown'
import PayloadDropdown from './components/InputDropdown'
import JsonViewer from './components/JsonView'
import KeyValueDropdown from './components/KeyValueDropdown'
import SliderDropdown from './components/SliderDropdown'
import SplitDropdown from './components/SplitDropdown'
import VoicesDropdown from './components/VoiceDropdown'
import { flattenJSON, parse_data } from './utils/payload'

export default function Event() {
  const { id } = useParams()
  const apiKey = getApiKey()
  const orgId = getOrgId()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [updating, setUpdating] = useState(false)
  const [previewModal, setPreviewModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [manualTriggerOpen, setManualTriggerOpen] = useState(false)
  const [testmode, setTestmode] = useState(false)
  const [cdModal, setCdModal] = useState(false) // custom deployment modal;

  const [loadingCd, setLoadingCd] = useState(false)
  const [encryptedKeys, setEncryptedKeys] = useState(null)
  const [encryptedKey, setEncryptedKey] = useState(null)
  const [manualTriggerBody, setManualTriggerBody] = useState({
    phone: '+1...',
    name: 'John Doe',
  })

  const {
    inboundNumbers,
    twilioNumbers,
    loading: phoneNumbersLoading,
    error: phoneNumbersError,
  } = usePhoneNumbers()

  const EVENT_BASE_URL = `${config.API_URL}/api/events`

  const methods = useForm({
    defaultValues: {
      event_id: null,
      event_history: null,
      event: null,
      selected_payload: null,
      event_base_endpoint: 'api.bland.ai',
      /// // SEND CALL PARAMS ////
      from: {
        using_key: false,
        value: '',
      },
      phone_number: {
        using_key: false,
        value: '',
      },
      model: {
        using_key: false,
        value: 'base',
      },
      task: {
        using_key: false,
        value: '',
      },
      webhook: {
        using_key: false,
        value: '',
      },
      first_sentence: {
        using_key: false,
        value: '',
      },
      voice: {
        using_key: false,
        value: 'e1289219-0ea2-4f22-a994-c542c2a48a0f',
        options: [],
      },
      background_track: {
        using_key: false,
        value: 'none',
      },
      voicemail_message: {
        using_key: false,
        value: '',
      },
      voicemail_action: {
        using_key: false,
        value: 'hangup',
        options: [
          {
            label: 'Hangup',
            value: 'hangup',
          },
          {
            label: 'Ignore',
            value: 'ignore',
          },
          {
            label: 'Leave Message',
            value: 'leave_message',
          },
          {
            label: 'Leave SMS',
            value: 'leave_sms',
          },
        ],
      },
      transfer_phone_number: {
        using_key: false,
        value: '',
      },
      sensitive_voicemail_detection: {
        using_key: false,
        value: false,
      },
      temperature: {
        using_key: false,
        value: 0.7,
      }, // impacted
      interruption_threshold: {
        using_key: false,
        value: 50,
      }, // impacted
      max_duration: {
        using_key: false,
        value: 30,
      },
      record: {
        using_key: false,
        value: false,
      },
      wait_for_greeting: {
        using_key: false,
        value: false,
      },
      block_interruptions: {
        using_key: false,
        value: false,
      },
      noise_cancellation: {
        using_key: false,
        value: false,
      },
      answered_by_enabled: {
        using_key: false,
        value: false,
      },
      amd: {
        using_key: false,
        value: false,
      },
      language: {
        using_key: false,
        value: 'en-US',
        options: [
          {
            value: 'en-US',
            label: 'English (US)',
          },
          {
            value: 'eng-GB',
            label: 'English (UK)',
          },
          {
            value: 'eng-AU',
            label: 'English (Australia)',
          },
          {
            value: 'eng-NZ',
            label: 'English (New Zealand)',
          },
          {
            value: 'eng-IN',
            label: 'English (New India)',
          },
          {
            value: 'es',
            label: 'Spanish',
          },
          {
            value: 'fr',
            label: 'French',
          },
          {
            value: 'de',
            label: 'German',
          },
          {
            value: 'hi',
            label: 'Hindi',
          },
          {
            value: 'ja',
            label: 'Japanese',
          },
          {
            value: 'it',
            label: 'Italian',
          },
          {
            value: 'pt',
            label: 'Portuguese',
          },
          {
            value: 'pt-BR',
            label: 'Portuguese (Brazil)',
          },
          {
            value: 'zh',
            label: 'Chinese (Mandarin, Simplified)',
          },
        ],
      },
      pathway_id: {
        using_key: false,
        value: '',
        options: [
          {
            value: 'somepathwayid',
            label: 'Booking Pathway',
          },
          {
            value: 'ipsumloremid',
            label: 'Reservations Handler',
          },
        ],
      },
      tools: {
        using_key: false,
        value: '',
        options: [],
      },
      request_data: [],
      transfer_list: [],
      analysis_schema: [],
      metadata: [],
      summary_prompt: {
        using_key: false,
        value: '',
      },
      analysis_prompt: {
        using_key: false,
        value: '',
      },
      analysis_preset: {
        using_key: false,
        value: '',
      },
    },
  })

  const { data: voices, isFetching: isFetchingVoices } = useVoices()
  const event = methods.watch('event')
  const event_history = methods.watch('event_history')
  const selected_payload = methods.watch('selected_payload')

  const handleTestCall = async () => {
    try {
      setLoading(true)

      if (!selected_payload) {
        toast.error(
          'To test this schema you must select a request from the dropdown below.',
        )
        return setLoading(false)
      }
      const payload = getPreviewData()
      if (payload?.webhook === '') {
        delete payload.webhook
      }

      if (payload?.summary_prompt === '') {
        delete payload?.summary_prompt
      }

      if (payload?.analysis_prompt === '') {
        delete payload?.analysis_prompt
      }

      if (payload?.analysis_preset === '') {
        delete payload?.analysis_preset
      }

      if (payload?.pathway_id === '') {
        delete payload?.pathway_id
      }

      if (payload?.from === '') {
        delete payload?.from
      }

      if (payload?.tools?.length || 0 === 0 || payload?.tools === '') {
        delete payload?.tools
      }

      if (payload?.background_track === '' || payload?.background_track === 'none') {
        delete payload?.background_track
      }

      if (
        payload?.transfer_phone_number === ''
        || !payload?.transfer_phone_number
      ) {
        delete payload?.transfer_phone_number
      }

      const response = await fetch(
        `https://${methods.getValues('event_base_endpoint') || 'api.bland.ai'}/v1/calls`,
        {
          method: 'POST',
          headers: {
            'authorization': apiKey,
            'content-type': 'application/json',
            ...(orgId && { 'x-bland-org-id': orgId }),
          },
          body: JSON.stringify(payload),
        },
      )

      if (response.ok && response.status === 200) {
        const data = await response.json()
        // console.log(data);
        if (data.status === 'success') {
          setLoading(false)
          setPreviewModal(false)
          return toast.success('Test call dispatched!')
        }
      }

      setLoading(false)
    }
    catch (error) {
      setLoading(false)
      return toast.error('Issue dispatching test call.')
    }
  }

  const handleUpdateDeploymentUrl = async () => {
    try {
      setLoadingCd(true)
      const response = await fetch(
        `${EVENT_BASE_URL}/${event.id}/custom_deployment`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'authorization': apiKey,
            ...(orgId && { 'x-bland-org-id': orgId }),
          },
          body: JSON.stringify({
            endpoint: methods.getValues('event_base_endpoint'),
          }),
        },
      )

      setCdModal(false)
      setLoadingCd(false)
      if (response.ok && response.status === 200) {
        return toast.success('Updated Custom Deployment')
      }
      else {
        throw new Error()
      }
    }
    catch (error) {
      methods.setValue('event_base_endpoint', 'api.bland.ai')
      return toast.error('Unexpected Error Saving')
    }
  }

  const updateUserEvent = async () => {
    try {
      setUpdating(true)
      const response = await fetch(
        `${config.API_URL}/api/events/${event.id}/update`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'authorization': apiKey,
            ...(orgId && { 'x-bland-org-id': orgId }),
          },
          body: JSON.stringify({
            ...handleSaveConfiguration(),
          }),
        },
      )

      if (response.ok && response.status === 200) {
        const data = await response.json()
        await loadEventById(event.id)
      }

      setUpdating(false)
    }
    catch (error) {
      setUpdating(false)
    }
  }

  const getUserPathways = async () => {
    try {
      const response = await fetch(`${config.API_URL}/convo_pathway/get`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'authorization': getAuthToken(),
          ...(orgId && { 'x-bland-org-id': orgId }),
        },
      })

      if (response.ok) {
        const data = await response.json()
        console.log('data from pathways', data)
        const pathwaysMap = data?.data?.data
        // Need to parse the JSON response
        if (pathwaysMap && Array.isArray(pathwaysMap)) {
          // Check if data exists and is an array
          const mappedPathways = pathwaysMap.map(doc => ({
            value: doc.id,
            label: doc.name,
          }))
          console.log('mappedPathways', mappedPathways)

          methods.setValue('pathway_id.options', mappedPathways)
        }
      }
    }
    catch (error) {
      console.error('Error fetching pathways:', error)
    }
  }

  const getUserTools = async () => {
    const response = await fetch(`${config.API_URL}/v1/tools`, {
      method: 'GET',
      headers: {
        cache: 'no-cache',
        authorization: apiKey,
        ...(orgId && { 'x-bland-org-id': orgId }),
      },
    })
    if (response.status === 200) {
      const data = await response.json()
      if (data?.tools?.length > 0) {
        // setFocusedTool(data.tools[0]);
        data.tools.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        )
        const mappedTools = data.tools?.map(doc => ({
          value: doc?.tool_id,
          label: doc?.tool?.name,
        }))
        methods.setValue('tools.options', mappedTools)
      }
    }
  }

  const loadEventReqHistoryById = async (id) => {
    try {
      const response = await fetch(
        `${config.API_URL}/api/events/${id}/history`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'Application/json',
            'authorization': apiKey,
            ...(orgId && { 'x-bland-org-id': orgId }),
          },
        },
      )

      if (response.ok && response.status === 200) {
        const data = await response.json()
        if (data.status === 'success') {
          const _history = data.history
          if (_history?.length > 0) {
          }
          _history.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          )
          methods.setValue('event_history', _history || [])

          if (_history?.length > 0 && !selected_payload) {
            // console.log(_history[0]);
            methods.setValue('selected_payload', _history[0].raw_data)
          }
        }
      }
    }
    catch (error) {
      methods.setValue('event_history', [])
    }
  }

  const loadEventById = async (id) => {
    const response = await fetch(`${EVENT_BASE_URL}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'Application/json',
        'authorization': apiKey,
        ...(orgId && { 'x-bland-org-id': orgId }),
      },
    })

    if (response.ok && response.status === 200) {
      const data = await response.json()
      if (data?.status === 'success') {
        const _event = data.event
        setTestmode(_event?.dispatch_call)
        methods.setValue('event_base_endpoint', _event?.endpoint)
        methods.setValue('event', _event)
        if (data?.event?.encrypted_key) {
          setEncryptedKey(data?.event?.encrypted_key)
        }

        if (data?.event?.event_schema) {
          const event_schema = data?.event?.event_schema || {}
          handleEventSchema(event_schema)
        }
      }
    }
  }

  const handleOnUpdateTestMode = async (toggle) => {
    try {
      const updateState = !toggle
      const response = await fetch(`${EVENT_BASE_URL}/${event?.id}/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'Application/json',
          'authorization': apiKey,
          ...(orgId && { 'x-bland-org-id': orgId }),
        },
        body: JSON.stringify({
          toggle_dispatch: updateState,
        }),
      })

      setTestmode(updateState)
    }
    catch (error) {}
  }

  const qpSearchAndSet = () => {
    methods.setValue('event_id', id)
    return id
  }

  const handleEventSchema = (event_schema) => {
    const dd = methods.getValues()
    // const _data = dd;

    // handling sensitive_voicemail_detection safely -> we may not even need this, but just to be safe.
    if (
      event_schema
      && (event_schema?.sensitive_voicemail_detection === null
        || event_schema?.sensitive_voicemail_detection === undefined)
    ) {
      event_schema.sensitive_voicemail_detection = {
        using_key: false,
        value: false,
      }
    }

    const data = {
      from: event_schema.from,
      amd: event_schema.amd,
      analysis_schema: event_schema.analysis_schema,
      analysis_prompt: event_schema.analysis_prompt,
      analysis_preset: event_schema.analysis_preset,
      answered_by_enabled: event_schema.answered_by_enabled,
      block_interruptions: event_schema.block_interruptions,
      noise_cancellation: event_schema.noise_cancellation,
      sensitive_voicemail_detection: event_schema.sensitive_voicemail_detection,
      first_sentence: event_schema.first_sentence,
      interruption_threshold:
        typeof event_schema.interruption_threshold === 'object'
          ? event_schema.interruption_threshold
          : {
              using_key: false,
              value: event_schema.interruption_threshold,
            },
      language: {
        using_key: event_schema.language.using_key,
        value: event_schema.language.value,
        options: dd.language.options,
      },
      max_duration: event_schema.max_duration,
      metadata: event_schema.metadata,
      model: event_schema.model,
      pathway_id: {
        using_key: event_schema.pathway_id.using_key,
        value: event_schema.pathway_id.value,
        options: dd.pathway_id.options,
      },
      phone_number: event_schema.phone_number,
      record: event_schema.record,
      request_data: event_schema.request_data,
      summary_prompt: event_schema.summary_prompt,
      task: event_schema.task,
      temperature:
        typeof event_schema.temperature === 'object'
          ? event_schema.temperature
          : {
              using_key: false,
              value: event_schema.temperature,
            },
      tools: {
        using_key: event_schema.tools.using_key,
        value: event_schema.tools.value,
        options: dd.tools.options,
      },
      transfer_list: event_schema.transfer_list,
      transfer_phone_number: event_schema.transfer_phone_number,
      voice: {
        using_key: event_schema.voice.using_key,
        value: event_schema.voice.value,
        options: dd.voice.options,
      },
      background_track: event_schema.background_track || {
        using_key: false,
        value: 'none',
      },
      voicemail_message: event_schema.voicemail_message,
      voicemail_action: {
        using_key: event_schema.voicemail_action.using_key,
        value: event_schema.voicemail_action.value,
        options: dd.voicemail_action.options,
      },
      wait_for_greeting: event_schema.wait_for_greeting,
      webhook: event_schema.webhook,
    }

    // special handling for analysis preset that was previously set incorrectly;
    if (
      typeof data.analysis_preset === 'string'
      || typeof data.analysis_preset !== 'object'
    ) {
      const val = data.analysis_preset !== null ? data.analysis_preset : ''
      data.analysis_preset = {
        using_key: false,
        value: val,
      }
    }

    methods.reset({
      ...methods.getValues(),
      ...data,
    })
  }

  const handleSaveConfiguration = () => {
    const dd = methods.watch()
    const _data = dd
    const data = {
      from: _data.from,
      amd: _data.amd,
      analysis_schema: _data.analysis_schema,
      analysis_prompt: _data.analysis_prompt,
      analysis_preset: _data.analysis_preset,
      sensitive_voicemail_detection: _data.sensitive_voicemail_detection,
      answered_by_enabled: _data.answered_by_enabled,
      block_interruptions: _data.block_interruptions,
      noise_cancellation: _data.noise_cancellation,
      first_sentence: _data.first_sentence,
      interruption_threshold: _data.interruption_threshold,
      language: {
        using_key: _data.language.using_key,
        value: _data.language.value,
      },
      max_duration: _data.max_duration,
      metadata: _data.metadata,
      model: _data.model,
      pathway_id: {
        using_key: _data.pathway_id.using_key,
        value: _data.pathway_id.value,
      },
      phone_number: _data.phone_number,
      record: _data.record,
      request_data: _data.request_data,
      summary_prompt: _data.summary_prompt,
      task: _data.task,
      temperature: _data.temperature,
      tools: {
        using_key: _data.tools.using_key,
        value: _data.tools.value,
      },
      transfer_list: _data.transfer_list,
      transfer_phone_number: _data.transfer_phone_number,
      voice: {
        using_key: _data.voice.using_key,
        value: _data.voice.value,
      },
      background_track: _data.background_track,
      voicemail_message: _data.voicemail_message,
      voicemail_action: {
        using_key: _data.voicemail_action.using_key,
        value: _data.voicemail_action.value,
      },
      wait_for_greeting: _data.wait_for_greeting,
      webhook: _data.webhook,
    }

    if (encryptedKey) {
      data.encrypted_key = encryptedKey
    }

    return data
  }

  const getPreviewData = () => {
    const schema = handleSaveConfiguration()
    const raw_payload = selected_payload || {}
    const json = parse_data(schema, raw_payload)
    return json || {}
  }

  const loadEncryptedKeys = async () => {
    try {
      const response = await $fetch('/byot/get_keys', {
        method: 'GET',
      })

      if (!response.errors) {
        const data = response.data
        if (Array.isArray(data) && data?.length > 0) {
          setEncryptedKeys(data)
        }
      }
    }
    catch (error) {}
  }

  useEffect(() => {
    const val = qpSearchAndSet()
    if (val) {
      loadEventReqHistoryById(val)
      loadEventById(val)
    }
  }, [navigate])

  useEffect(() => {
    const interval = setInterval(() => {
      if (event?.id) {
        loadEventReqHistoryById(event?.id)
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    getUserTools()
    getUserPathways()
    loadEncryptedKeys()
  }, [])

  const customStyles = {
    control: provided => ({
      ...provided,
      'borderColor': '#ced4da',
      'width': 'calc(100% + 10px)',
      'boxShadow': 'none',
      '&:hover': {
        borderColor: '#80bdff',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#007bff'
        : state.isFocused
          ? '#e9ecef'
          : null,
      color: state.isSelected ? 'white' : 'black',
      padding: 10,
    }),
    menu: provided => ({
      ...provided,
      zIndex: 9999,
    }),
  }

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  }

  useEffect(() => {
    if (voices && voices.length > 0) {
      // Separate options based on "Bland Curated" tag
      const mappedVoices = voices.map(doc => ({
        value: doc.id,
        label: doc.name,
        type: doc?.tags?.includes('Bland Curated')
          ? 'bland_curated'
          : 'user_voices',
      }))

      methods.setValue('voice.options', mappedVoices)
    }
  }, [voices, isFetchingVoices])

  if (!event || !event_history)
    return <div />

  return (
    <div className="flex h-full max-h-full flex-col gap-5 p-7 pb-0">
      <FormProvider {...methods}>
        <div className="flex h-full max-h-full min-h-0 flex-col gap-5">
          <TopBarContent>
            <TopBar.Title>
              <div
                onClick={() => navigate('/dashboard/all-events')}
                className="cursor-pointer"
              >
                Events
                {event && `/ ${event.event_name}`}
              </div>
            </TopBar.Title>
            <TopBar.Actions>
              <RACButton
                onClick={() => {
                  navigator.clipboard.writeText(event?.id)
                }}
                variant="secondary"
                size="sm"
                className="mr-2.5"
              >
                <img src={CopyIcon} className="h-2.5 w-2.5" alt="" />
                Copy Event Id
              </RACButton>

              <RACButton
                onClick={() => setPreviewModal(true)}
                variant="secondary"
                size="sm"
                className="mr-2.5"
              >
                <img
                  src={ExpandIcon}
                  className="h-2.5 w-2.5"
                  alt=""
                />
                Preview Call Data
              </RACButton>

              <RACButton
                isLoading={updating}
                onClick={() => updateUserEvent()}
                variant="primary"
                size="sm"
              >
                Save Event
              </RACButton>
            </TopBar.Actions>
          </TopBarContent>
          {/* Fixed Event Handler URL Section */}
          <div className="border-b border-gray-200 bg-white pb-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-black">Event Handler URL</h3>
                <div className="mt-1 flex items-center gap-1">
                  <span className={`
                    rounded bg-gray-200 px-1 py-0.5 text-xs font-medium
                  `}
                  >
                    POST
                  </span>
                  <span className="text-xs text-gray-600">data from your server using</span>
                  <span className={`
                    rounded bg-gray-200 px-1 py-0.5 text-xs font-medium
                  `}
                  >
                    application/json
                  </span>
                  <span className="text-xs text-gray-600">
                    to the Event Handler URL below to automatically dispatch
                    calls based on your schema.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <input
                    disabled
                    value={`${EVENT_BASE_URL}/${event.id}/handler`}
                    className={`
                      w-full cursor-default rounded border border-gray-300 px-2
                      py-1.5 text-sm text-gray-500
                    `}
                  />
                  <div
                    onClick={() => setCdModal(true)}
                    className="mt-1 flex cursor-pointer items-center gap-1"
                  >
                    <span className="text-xs text-gray-600">
                      Calls will use deployment:
                    </span>
                    <span className={`
                      rounded bg-gray-200 px-1 py-0.5 text-xs font-medium
                    `}
                    >
                      {methods.getValues('event_base_endpoint') || 'api.bland.ai'}
                    </span>
                    <img
                      src={SettingIcon}
                      className="h-2.5 w-2.5 cursor-pointer"
                      alt="settings"
                    />
                  </div>
                </div>

                <Button
                  onClick={() => setCdModal(true)}
                  appearance="outline"
                  className="flex h-8 w-10 items-center justify-center p-0"
                >
                  <img
                    src={SettingIcon}
                    className="h-3.5 w-3.5"
                    alt=""
                  />
                </Button>

                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${EVENT_BASE_URL}/${event.id}/handler`,
                    )
                  }}
                  appearance="outline"
                  className="flex h-8 w-10 items-center justify-center p-0"
                >
                  <img src={CopyIcon} className="h-3.5 w-3.5" alt="" />
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content Grid - positioned below fixed header */}

          <div className="grid min-h-0 flex-1 grid-cols-2 gap-6">
            {/* Left Column - Configure Call */}
            <div className="flex flex-col overflow-y-auto">
              <div className="mb-4">
                <h2 className="text-sm font-medium text-black">
                  Configure Call
                </h2>
                <p className="mt-0 text-xs text-gray-600">
                  Connect each field to the payload, enter a value manually, or
                  combine payload fields and manual values using
                  &#123;&#123;variable_name&#125;&#125; notation
                </p>
              </div>

              <div className="flex-1 space-y-2.5 overflow-y-auto">
                <PayloadDropdown
                  keypairs={flattenJSON(selected_payload)}
                  methods={methods}
                  fieldname="phone_number"
                  label="Phone Number"
                />

                <div className="mb-2.5 rounded border border-gray-200 p-4">
                  <PhoneNumberSelector
                    fieldName="from.value"
                    userNumbers={inboundNumbers}
                    byotNumbers={twilioNumbers}
                    loading={phoneNumbersLoading}
                    error={phoneNumbersError}
                  />
                </div>

                {(methods.watch('from.value') || encryptedKey) && (
                  <HookInputDropdown
                    labelValueList={encryptedKeys?.map(item => ({
                      label: 'Encrypted key',
                      value: item.id,
                    }))}
                    label="Encrypted Key"
                    value={encryptedKey}
                    placeholder="Encrypted Key"
                    onChange={value => setEncryptedKey(value)}
                  />
                )}

                <PayloadDropdown
                  keypairs={flattenJSON(selected_payload)}
                  methods={methods}
                  fieldname="task"
                  label="Prompt"
                  prompt
                />

                <div className="mb-2.5 rounded border border-gray-200 p-4">
                  <Model fieldName="model.value" />
                </div>

                <VoicesDropdown
                  keypairs={flattenJSON(selected_payload)}
                  nested_options={methods.getValues('voice.options')}
                  fieldname="voice"
                  label="Voice"
                  apiKey={apiKey}
                />

                <BackgroundDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="background_track"
                  label="Background Audio"
                />

                <SplitDropdown
                  keypairs={flattenJSON(selected_payload)}
                  nested_options={methods.getValues('language.options')}
                  fieldname="language"
                  label="Language"
                  tab_name="Languages"
                />

                <div className="mb-2.5 rounded border border-gray-200 p-4">
                  <label className="mb-0.5 block text-xs font-medium text-black">
                    Temperature
                  </label>
                  <SliderDropdown
                    keypairs={flattenJSON(selected_payload)}
                    fieldname="temperature"
                    methods={methods}
                    label="Temperature"
                  />
                </div>

                <div className="mb-2.5 rounded border border-gray-200 p-4">
                  <label className="mb-0.5 block text-xs font-medium text-black">
                    Interruption Threshold
                  </label>
                  <SliderDropdown
                    keypairs={flattenJSON(selected_payload)}
                    fieldname="interruption_threshold"
                    methods={methods}
                    label="Interruption Threshold"
                  />
                </div>

                <div className="mb-2.5 rounded border border-gray-200 p-4">
                  <label className="mb-2.5 block text-xs font-medium text-black">
                    Max Duration (mins)
                  </label>
                  <input
                    defaultValue={30}
                    type="number"
                    value={methods.getValues('max_duration.value')}
                    onChange={(event) => {
                      if ((Number(event.target.value) || 0) > 60)
                        return
                      methods.setValue(
                        'max_duration.value',
                        Number(event.target.value) || 0,
                      )
                    }}
                    className={`
                      w-full rounded border border-gray-300 px-2 py-1.5 text-xs
                    `}
                  />
                </div>

                <SplitDropdown
                  keypairs={flattenJSON(selected_payload)}
                  nested_options={methods.getValues('pathway_id.options')}
                  fieldname="pathway_id"
                  label="Pathway"
                  tab_name="Pathways"
                />

                <SplitDropdown
                  keypairs={flattenJSON(selected_payload)}
                  nested_options={methods.getValues('tools.options')}
                  fieldname="tools"
                  label="Tools"
                  tab_name="Tools"
                />

                <KeyValueDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="request_data"
                  label="Request Data"
                />

                <div className="mb-2.5 rounded border border-gray-200 p-4">
                  <AnalysisPreset
                    fieldName="analysis_preset"
                    isEventPage
                  />
                </div>

                <KeyValueDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="analysis_schema"
                  label="Analysis Schema"
                />

                <BooleanDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="record"
                  label="Record"
                />

                <BooleanDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="wait_for_greeting"
                  label="Wait For Greeting"
                />

                <BooleanDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="answered_by_enabled"
                  label="Answered By"
                />

                <BooleanDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="amd"
                  label="Enable IVR Mode"
                />

                <BooleanDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="sensitive_voicemail_detection"
                  label="Sensitive Voicemail Detection"
                />

                <BooleanDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="block_interruptions"
                  label="Block Interruptions"
                />

                <BooleanDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="noise_cancellation"
                  label="Noise Cancellation"
                />

                <PayloadDropdown
                  keypairs={flattenJSON(selected_payload)}
                  methods={methods}
                  fieldname="first_sentence"
                  label="First Sentence"
                />

                <PayloadDropdown
                  keypairs={flattenJSON(selected_payload)}
                  methods={methods}
                  fieldname="voicemail_message"
                  label="Voicemail Message"
                />

                <SplitDropdown
                  keypairs={flattenJSON(selected_payload)}
                  nested_options={methods.getValues('voicemail_action.options')}
                  fieldname="voicemail_action"
                  label="Voicemail Action"
                  tab_name="Voicemail Actions"
                />

                <PayloadDropdown
                  keypairs={flattenJSON(selected_payload)}
                  methods={methods}
                  fieldname="transfer_phone_number"
                  label="Transfer Phone Number"
                />

                <KeyValueDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="transfer_list"
                  label="Transfer List"
                />

                <PayloadDropdown
                  keypairs={flattenJSON(selected_payload)}
                  methods={methods}
                  fieldname="summary_prompt"
                  label="Summary Prompt"
                />

                <PayloadDropdown
                  keypairs={flattenJSON(selected_payload)}
                  methods={methods}
                  fieldname="analysis_prompt"
                  label="Analysis Prompt"
                />

                <PayloadDropdown
                  keypairs={flattenJSON(selected_payload)}
                  methods={methods}
                  fieldname="webhook"
                  label="Webhook URL"
                />

                <KeyValueDropdown
                  keypairs={flattenJSON(selected_payload)}
                  fieldname="metadata"
                  label="Metadata"
                />
              </div>
            </div>

            {/* Right Column - Requests */}
            <div className={`
              flex min-h-0 flex-col overflow-hidden border-l border-gray-200
              pl-6
            `}
            >
              <div className="border-b border-gray-200 pb-4">
                <div className="flex w-full items-center justify-between">
                  <div>
                    <h2 className="text-sm font-medium text-black">
                      Requests
                    </h2>
                    <p className="text-xs text-gray-600">
                      This is where you can view and manage incoming requests.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <img
                      src={ListeningGif}
                      height={20}
                      width={20}
                      alt="listening"
                    />
                    <Button
                      onClick={() => {
                        loadEventReqHistoryById(event.id)
                      }}
                      appearance="outline"
                      className="flex h-8 w-10 items-center justify-center p-0"
                    >
                      <img
                        src={RefreshIcon}
                        className="h-3.5 w-3.5"
                        alt=""
                      />
                    </Button>
                  </div>
                </div>

                <div className="mt-2.5 flex w-full items-center justify-between">
                  <div>
                    <h3 className="text-xs font-normal text-black">
                      Test Mode
                    </h3>
                    <p className="text-xs text-gray-600">
                      This will allow you to recieve data but not dispatch a
                      call on event.
                    </p>
                  </div>
                  <CustomSwitch
                    isOn={testmode}
                    handleToggle={() => handleOnUpdateTestMode(testmode)}
                  />
                </div>

                <div className="mt-2.5">
                  <h3 className="mb-2.5 text-xs font-normal text-black">
                    Incoming Request History
                  </h3>
                  <Select
                    styles={customStyles}
                    searchable={false}
                    direction="ltr"
                    onChange={(value) => {
                      const found = event_history?.find(
                        doc => doc.request_id === value[0].value,
                      )?.raw_data
                      if (found.request_id === value[0].value) {
                        return methods.setValue('selected_payload', null)
                      }
                      methods.setValue('selected_payload', found)
                    }}
                    options={event_history?.map((val, ind) => ({
                      value: val.request_id,
                      label: `${formatTimestamp(val.created_at)}${ind === 0 ? '- (latest)' : ''}`,
                      createdAt: val.created_at,
                    }))}
                    placeholder="Select an option..."
                    noOptionsMessage={() => 'No options available'}
                    noDataLabel={`Listening for events at: ${EVENT_BASE_URL}/${event.id}/handler`}
                    formatOptionLabel={(option, index) => (
                      <div>
                        <div style={{ fontSize: 'small', color: '#6c757d' }}>
                          {index === 0 ? ' (latest)' : ''}
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>

              <Button
                appearance="outline"
                className="h-8 rounded"
                onClick={() => {
                  setManualTriggerOpen(true)
                }}
              >
                <p className="text-xs">Trigger Request Manually</p>
              </Button>

              <Modal
                open={manualTriggerOpen}
                onClose={() => setManualTriggerOpen(false)}
              >
                <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                  Manual Trigger
                </p>
                <p
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: 'darkgrey',
                    marginBottom: 10,
                  }}
                >
                  Send a test request to the event handler - then the Event
                  knows how your data is structured.
                </p>
                <p
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: 'darkgrey',
                    marginBottom: 10,
                  }}
                >
                  Then, you'll be able to use that data in your call
                  configuration to change how each call is dispatched.
                </p>
                <Editor
                  height="30vh"
                  defaultLanguage="json"
                  defaultValue={JSON.stringify(manualTriggerBody, null, 2)}
                  onChange={value => setManualTriggerBody(value)}
                />
                <Button
                  onClick={() => {
                    try {
                      const json
                        = typeof manualTriggerBody === 'string'
                          ? JSON.parse(manualTriggerBody)
                          : manualTriggerBody
                      // console.log(json);
                      fetch(`${EVENT_BASE_URL}/${event.id}/handler`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': apiKey,
                          ...(orgId && { 'x-bland-org-id': orgId }),
                        },
                        body: JSON.stringify(json),
                      })
                        .then(res => res.data)
                        .then((data) => {
                          // console.log(data);
                          setManualTriggerOpen(false)
                          setTimeout(() => {
                            loadEventReqHistoryById(event.id)
                          }, 1000)
                        })
                    }
                    catch (error) {
                      // console.log(error);
                      toast.error('Invalid JSON')
                    }
                  }}
                >
                  Send
                </Button>
              </Modal>

              <div className="flex-1 overflow-y-auto">
                <JsonViewer
                  data={
                    selected_payload
                    || event_history?.[event_history?.length - 1]?.raw_data
                    || {}
                  }
                />
              </div>
            </div>
          </div>

          <Modal open={previewModal} onClose={() => setPreviewModal(false)}>
            <div className="flex w-full items-center justify-between">
              <p className="m-0 p-0 text-base font-medium">
                Preview your call...
              </p>
              <img
                onClick={() => setPreviewModal(false)}
                className="cursor-pointer"
                src={CloseIcon}
                alt="close"
              />
            </div>

            <p className="mb-2.5 text-xs font-light text-gray-600">
              This is the payload that gets sent to dispatch a call based on
              your configured data. Tell your call based on configuration below.
            </p>
            <JsonViewer data={getPreviewData()} />

            <Button
              loading={loading}
              onClick={() => handleTestCall()}
              className="mt-2.5 rounded"
            >
              <p>Test Call</p>
            </Button>
          </Modal>

          <Modal open={cdModal} onClose={() => setCdModal(false)}>
            <div className="flex w-full items-center justify-between">
              <p className="m-0 p-0 text-base font-medium">
                Custom Deployment
              </p>
              <img
                onClick={() => setCdModal(false)}
                className="cursor-pointer"
                src={CloseIcon}
                alt="close"
              />
            </div>

            <div className="mt-5">
              <input
                type="text"
                value={methods.watch('event_base_endpoint')}
                onChange={e =>
                  methods.setValue('event_base_endpoint', e.target.value)}
                placeholder="api.bland.ai"
                className={`
                  w-full rounded border border-gray-300 px-2 py-1.5 text-xs
                `}
              />

              <p
                onClick={() =>
                  methods.setValue('event_base_endpoint', 'api.bland.ai')}
                className="mt-2.5 cursor-pointer text-xs font-normal underline"
              >
                Use Default Endpoint
              </p>
            </div>

            <Button
              loading={loadingCd}
              onClick={() => handleUpdateDeploymentUrl()}
              className="mt-5 h-8 rounded"
            >
              <p>Save Custom Deployment</p>
            </Button>
          </Modal>
        </div>
      </FormProvider>
    </div>
  )
}

function CustomSwitch({ isOn, handleToggle }) {
  return (
    <div className="inline-flex cursor-pointer items-center" onClick={handleToggle}>
      <label className="relative inline-block h-5 w-10 cursor-pointer">
        <input
          type="checkbox"
          checked={isOn}
          readOnly
          className="peer h-0 w-0 cursor-pointer opacity-0"
        />
        <span className={`
          absolute inset-0 cursor-pointer rounded-full bg-gray-300
          transition-all duration-300
          peer-checked:bg-blue-600 peer-checked:before:translate-x-5
          before:absolute before:bottom-0.5 before:left-0.5 before:h-4
          before:w-4 before:rounded-full before:bg-white before:transition-all
          before:duration-300 before:content-['']
        `}
        />
      </label>
    </div>
  )
}
